import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Spinner } from "./ui/shadcn-io/spinner";
import { ArrowUp } from "lucide-react";
import { Employee } from "../lib/employeeTableColumns";

interface MissingFields {
  totalPaidLeaves?: boolean;
  remainingPaidLeaves?: boolean;
  designation?: boolean;
  department?: boolean;
  panNumber?: boolean;
  accountNumber?: boolean;
  ifsc?: boolean;
  bankName?: boolean;
  branch?: boolean;
  phone?: boolean;
  doj?: boolean;
}

interface EmployeeData {
  totalPaidLeaves?: number;
  remainingPaidLeaves?: number;
  designation?: string;
  department?: string;
  panNumber?: string;
  accountNumber?: string;
  ifsc?: string;
  bankName?: string;
  branch?: string;
  phone?: string;
  doj?: string;
}

interface UpgradeEmployeeDialogProps {
  employee: Employee;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export function UpgradeEmployeeDialog({
  employee,
  isOpen,
  onClose,
  onSuccess,
}: UpgradeEmployeeDialogProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [isFetchingFields, setIsFetchingFields] = useState(false);
  const [error, setError] = useState("");
  const [missingFields, setMissingFields] = useState<MissingFields>({});
  const [formData, setFormData] = useState<EmployeeData>({});
  const [existingData, setExistingData] = useState<EmployeeData>({});

  useEffect(() => {
    if (isOpen && employee.id) {
      fetchMissingFields();
    }
  }, [isOpen, employee.id]);

  const fetchMissingFields = async () => {
    try {
      setIsFetchingFields(true);
      setError("");
      const token = localStorage.getItem("authToken");

      // Fetch missing fields
      const fieldsResponse = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}api/employees/${employee.id}/upgrade-fields`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setMissingFields(fieldsResponse.data.missingFields || {});
      setExistingData(fieldsResponse.data.existingData || {});

      // Initialize form data with existing values where available
      const initialData: EmployeeData = {};
      if (fieldsResponse.data.existingData) {
        const existing = fieldsResponse.data.existingData;
        if (existing.designation) initialData.designation = existing.designation;
        if (existing.department) initialData.department = existing.department;
        if (existing.phone) initialData.phone = existing.phone;
        if (existing.panNumber) initialData.panNumber = existing.panNumber;
        if (existing.accountNumber) initialData.accountNumber = existing.accountNumber;
        if (existing.ifsc) initialData.ifsc = existing.ifsc;
        if (existing.bankName) initialData.bankName = existing.bankName;
        if (existing.branch) initialData.branch = existing.branch;
        if (existing.doj) initialData.doj = existing.doj;
      }
      setFormData(initialData);
    } catch (error: any) {
      console.error("Error fetching missing fields:", error);
      setError(
        error.response?.data?.message || "Failed to fetch required fields"
      );
    } finally {
      setIsFetchingFields(false);
    }
  };

  const handleInputChange = (field: keyof EmployeeData, value: string | number) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setIsLoading(true);

    try {
      const token = localStorage.getItem("authToken");

      // Prepare payload with only the fields that are being sent
      const payload: any = {};
      
      if (missingFields.totalPaidLeaves && formData.totalPaidLeaves !== undefined) {
        payload.totalPaidLeaves = Number(formData.totalPaidLeaves);
      }
      if (missingFields.remainingPaidLeaves && formData.remainingPaidLeaves !== undefined) {
        payload.remainingPaidLeaves = Number(formData.remainingPaidLeaves);
      }
      if (missingFields.designation && formData.designation) {
        payload.designation = formData.designation;
      }
      if (missingFields.department && formData.department) {
        payload.department = formData.department;
      }
      if (missingFields.panNumber && formData.panNumber) {
        payload.panNumber = formData.panNumber;
      }
      if (missingFields.accountNumber && formData.accountNumber) {
        payload.accountNumber = formData.accountNumber;
      }
      if (missingFields.ifsc && formData.ifsc) {
        payload.ifsc = formData.ifsc;
      }
      if (missingFields.bankName && formData.bankName) {
        payload.bankName = formData.bankName;
      }
      if (missingFields.branch && formData.branch) {
        payload.branch = formData.branch;
      }
      if (missingFields.phone && formData.phone) {
        payload.phone = formData.phone;
      }
      if (missingFields.doj && formData.doj) {
        payload.doj = formData.doj;
      }

      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}api/employees/${employee.id}/upgrade-to-fulltime`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      onSuccess();
      onClose();
    } catch (error: any) {
      console.error("Error upgrading employee:", error);
      setError(
        error.response?.data?.message || "Failed to upgrade employee"
      );
    } finally {
      setIsLoading(false);
    }
  };

  const hasRequiredFields = Object.values(missingFields).some((value) => value === true);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ArrowUp className="h-5 w-5" />
            Upgrade {employee.name} to Full-Time Employee
          </DialogTitle>
          <DialogDescription>
            Please provide the missing information required to upgrade this{" "}
            {employee.employeeType?.toLowerCase()} to a full-time employee.
            Fields that already have values are pre-filled.
          </DialogDescription>
        </DialogHeader>

        {isFetchingFields ? (
          <div className="flex items-center justify-center py-8">
            <div className="flex flex-col items-center gap-2">
              <Spinner variant="ellipsis" size={40} />
              <p className="text-sm text-muted-foreground">
                Loading required fields...
              </p>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <div className="p-4 text-red-600 bg-red-50 border border-red-200 rounded-md">
                {error}
              </div>
            )}

            {!hasRequiredFields && (
              <div className="p-4 text-green-600 bg-green-50 border border-green-200 rounded-md">
                All required fields are already available. You can proceed with the upgrade without entering additional information.
              </div>
            )}

            <div className="grid gap-4">
              {/* Leave Information */}
              {(missingFields.totalPaidLeaves || missingFields.remainingPaidLeaves) && (
                <div className="space-y-4 p-4 border rounded-lg">
                  <h3 className="font-semibold text-sm">Leave Information</h3>
                  <div className="grid grid-cols-2 gap-4">
                    {missingFields.totalPaidLeaves && (
                      <div className="grid gap-2">
                        <Label htmlFor="totalPaidLeaves">
                          Total Paid Leaves <span className="text-red-500">*</span>
                        </Label>
                        <Input
                          id="totalPaidLeaves"
                          type="number"
                          value={formData.totalPaidLeaves || ""}
                          onChange={(e) =>
                            handleInputChange(
                              "totalPaidLeaves",
                              e.target.value ? Number(e.target.value) : ""
                            )
                          }
                          placeholder="12"
                          disabled={isLoading}
                          required
                        />
                      </div>
                    )}

                    {missingFields.remainingPaidLeaves && (
                      <div className="grid gap-2">
                        <Label htmlFor="remainingPaidLeaves">
                          Remaining Paid Leaves <span className="text-red-500">*</span>
                        </Label>
                        <Input
                          id="remainingPaidLeaves"
                          type="number"
                          value={formData.remainingPaidLeaves || ""}
                          onChange={(e) =>
                            handleInputChange(
                              "remainingPaidLeaves",
                              e.target.value ? Number(e.target.value) : ""
                            )
                          }
                          placeholder="12"
                          disabled={isLoading}
                          required
                        />
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Basic Information */}
              {(missingFields.designation ||
                missingFields.department ||
                missingFields.phone ||
                missingFields.doj) && (
                <div className="space-y-4 p-4 border rounded-lg">
                  <h3 className="font-semibold text-sm">Basic Information</h3>
                  <div className="grid grid-cols-2 gap-4">
                    {missingFields.designation && (
                      <div className="grid gap-2">
                        <Label htmlFor="designation">
                          Designation <span className="text-red-500">*</span>
                        </Label>
                        <Input
                          id="designation"
                          value={formData.designation || ""}
                          onChange={(e) =>
                            handleInputChange("designation", e.target.value)
                          }
                          placeholder="Software Engineer"
                          disabled={isLoading}
                          required
                        />
                      </div>
                    )}

                    {missingFields.department && (
                      <div className="grid gap-2">
                        <Label htmlFor="department">
                          Department <span className="text-red-500">*</span>
                        </Label>
                        <Input
                          id="department"
                          value={formData.department || ""}
                          onChange={(e) =>
                            handleInputChange("department", e.target.value)
                          }
                          placeholder="Development"
                          disabled={isLoading}
                          required
                        />
                      </div>
                    )}

                    {missingFields.phone && (
                      <div className="grid gap-2">
                        <Label htmlFor="phone">
                          Phone <span className="text-red-500">*</span>
                        </Label>
                        <Input
                          id="phone"
                          value={formData.phone || ""}
                          onChange={(e) =>
                            handleInputChange("phone", e.target.value)
                          }
                          placeholder="+1234567890"
                          disabled={isLoading}
                          required
                        />
                      </div>
                    )}

                    {missingFields.doj && (
                      <div className="grid gap-2">
                        <Label htmlFor="doj">
                          Date of Joining <span className="text-red-500">*</span>
                        </Label>
                        <Input
                          id="doj"
                          type="date"
                          value={formData.doj || ""}
                          onChange={(e) =>
                            handleInputChange("doj", e.target.value)
                          }
                          disabled={isLoading}
                          required
                        />
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Bank Details */}
              {(missingFields.panNumber ||
                missingFields.accountNumber ||
                missingFields.ifsc ||
                missingFields.bankName ||
                missingFields.branch) && (
                <div className="space-y-4 p-4 border rounded-lg">
                  <h3 className="font-semibold text-sm">Bank Details</h3>
                  <div className="grid gap-4">
                    {missingFields.panNumber && (
                      <div className="grid gap-2">
                        <Label htmlFor="panNumber">
                          PAN Number <span className="text-red-500">*</span>
                        </Label>
                        <Input
                          id="panNumber"
                          value={formData.panNumber || ""}
                          onChange={(e) =>
                            handleInputChange("panNumber", e.target.value)
                          }
                          placeholder="ABCDE1234F"
                          disabled={isLoading}
                          required
                        />
                      </div>
                    )}

                    {missingFields.accountNumber && (
                      <div className="grid gap-2">
                        <Label htmlFor="accountNumber">
                          Account Number <span className="text-red-500">*</span>
                        </Label>
                        <Input
                          id="accountNumber"
                          value={formData.accountNumber || ""}
                          onChange={(e) =>
                            handleInputChange("accountNumber", e.target.value)
                          }
                          placeholder="1234567890"
                          disabled={isLoading}
                          required
                        />
                      </div>
                    )}

                    <div className="grid grid-cols-2 gap-4">
                      {missingFields.ifsc && (
                        <div className="grid gap-2">
                          <Label htmlFor="ifsc">
                            IFSC Code <span className="text-red-500">*</span>
                          </Label>
                          <Input
                            id="ifsc"
                            value={formData.ifsc || ""}
                            onChange={(e) =>
                              handleInputChange("ifsc", e.target.value)
                            }
                            placeholder="SBIN0001234"
                            disabled={isLoading}
                            required
                          />
                        </div>
                      )}

                      {missingFields.bankName && (
                        <div className="grid gap-2">
                          <Label htmlFor="bankName">
                            Bank Name <span className="text-red-500">*</span>
                          </Label>
                          <Input
                            id="bankName"
                            value={formData.bankName || ""}
                            onChange={(e) =>
                              handleInputChange("bankName", e.target.value)
                            }
                            placeholder="State Bank of India"
                            disabled={isLoading}
                            required
                          />
                        </div>
                      )}
                    </div>

                    {missingFields.branch && (
                      <div className="grid gap-2">
                        <Label htmlFor="branch">
                          Branch <span className="text-red-500">*</span>
                        </Label>
                        <Input
                          id="branch"
                          value={formData.branch || ""}
                          onChange={(e) =>
                            handleInputChange("branch", e.target.value)
                          }
                          placeholder="Mumbai Main Branch"
                          disabled={isLoading}
                          required
                        />
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
                disabled={isLoading}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isLoading || isFetchingFields}>
                {isLoading ? (
                  <div className="flex items-center gap-2">
                    <Spinner variant="ellipsis" size={20} />
                    <span>Upgrading...</span>
                  </div>
                ) : (
                  <>
                    <ArrowUp className="mr-2 h-4 w-4" />
                    {hasRequiredFields ? "Upgrade to Full-Time" : "Confirm Upgrade"}
                  </>
                )}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

